import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth } from './AuthContext';
import { organizationsApi } from '../lib/api';

const STORAGE_KEY = 'ultimategis.selectedOrg';

const OrgContext = createContext({
  organizations: [],
  selectedOrgId: '',
  selectedOrg: null,
  loading: true,
  setSelectedOrgId: () => {},
  refreshOrgs: () => {},
});

export function OrgProvider({ children }) {
  const { user, session } = useAuth();
  const userId = user?.id || session?.user?.id || null;
  const loadedRef = useRef(false);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgIdState] = useState('');
  const [loading, setLoading] = useState(true);

  const setSelectedOrgId = useCallback((id) => {
    setSelectedOrgIdState(id || '');
    try {
      if (id) {
        localStorage.setItem(STORAGE_KEY, id);
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (err) {
      // Storage can be unavailable (private mode, disabled cookies) — the
      // in-memory selection still works for the current session.
      console.error('Failed to persist selected organization', err);
    }
  }, []);

  const loadOrgs = useCallback(async () => {
    // Only show the loading state for the first load; background refreshes
    // keep the current orgs on screen instead of flashing skeletons.
    if (!loadedRef.current) setLoading(true);
    try {
      const res = await organizationsApi.list();
      const orgs = res.data || [];
      setOrganizations(orgs);

      let stored = null;
      try {
        stored = localStorage.getItem(STORAGE_KEY);
      } catch (err) {
        console.error('Failed to read stored organization', err);
      }

      if (stored && orgs.some(o => o.id === stored)) {
        setSelectedOrgIdState(stored);
      } else if (orgs.length > 0) {
        // Restore falls back to the first org — covers both "exactly one
        // org" (auto-select) and a stale/missing stored id.
        setSelectedOrgId(orgs[0].id);
      } else {
        setSelectedOrgIdState('');
      }
    } catch (err) {
      console.error('Failed to load organizations', err);
      setOrganizations([]);
      setSelectedOrgIdState('');
    } finally {
      loadedRef.current = true;
      setLoading(false);
    }
  }, [setSelectedOrgId]);

  useEffect(() => {
    if (userId) {
      loadedRef.current = false;
      loadOrgs();
    } else {
      loadedRef.current = false;
      // Logged out (or not yet authenticated) — clear org state so a
      // different user signing in doesn't inherit the previous selection.
      setOrganizations([]);
      setSelectedOrgIdState('');
      setLoading(false);
    }
  }, [userId, loadOrgs]);

  const selectedOrg = useMemo(
    () => organizations.find(o => o.id === selectedOrgId) || null,
    [organizations, selectedOrgId]
  );

  const refreshOrgs = useCallback(() => loadOrgs(), [loadOrgs]);

  const value = useMemo(() => ({
    organizations,
    selectedOrgId,
    selectedOrg,
    loading,
    setSelectedOrgId,
    refreshOrgs,
  }), [organizations, selectedOrgId, selectedOrg, loading, setSelectedOrgId, refreshOrgs]);

  return (
    <OrgContext.Provider value={value}>
      {children}
    </OrgContext.Provider>
  );
}

export function useOrg() {
  return useContext(OrgContext);
}

export default OrgContext;
